import React, { useState, useEffect } from 'react';
import {
  flattenFFEItems, isOrdered, isArrived, isInTransit, isAwaitingDelivery,
  summarize, getStatusColor, normalizeStatus,
} from '../utils/logisticsStatus';

const API_URL = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) + '/api';

// Minimal, self-contained carrier tracking-link helpers (no external util).
const CARRIER_TRACKING_URLS = {
  fedex: 'https://www.fedex.com/fedextrack/?trknbr=',
  ups: 'https://www.ups.com/track?tracknum=',
  usps: 'https://tools.usps.com/go/TrackConfirmAction?tLabels=',
  dhl: 'https://www.dhl.com/en/express/tracking.html?AWB=',
};
function getTrackingUrl(carrier, tracking) {
  if (!tracking) return null;
  const key = (carrier || '').toLowerCase().replace(/[^a-z]/g, '');
  const base = CARRIER_TRACKING_URLS[key];
  return base ? base + encodeURIComponent(tracking) : null;
}
function getCarrierLogo() { return '🚚'; }

/**
 * ShippingTracker — shipping/order status for FF&E items.
 * Reads the SAME canonical FF&E source as Critical Path & Delivery Scheduler
 * (GET /api/projects/{id}?sheet_type=ffe) and classifies off item.status via
 * the shared logisticsStatus helper, so the counts agree across all views and
 * stay in sync with the FF&E sheet. Carrier / tracking # / ETA are manual
 * fields shown as entered (no fabricated live status).
 */
export default function ShippingTracker({ projectId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadItems();
    const interval = setInterval(loadItems, 30000);
    return () => clearInterval(interval);
  }, [projectId]);

  const loadItems = async () => {
    try {
      const response = await fetch(`${API_URL}/projects/${projectId}?sheet_type=ffe`);
      if (response.ok) {
        const project = await response.json();
        setItems(flattenFFEItems(project).filter(isOrdered));
      }
    } catch (error) {
      console.error('Failed to load shipping items:', error);
    } finally {
      setLoading(false);
    }
  };

  const summary = summarize(items);

  const FILTERS = [
    { key: 'all', label: 'All', value: summary.total, color: '#D4A574', match: () => true },
    { key: 'in_transit', label: 'In Transit', value: summary.inTransit, color: '#6495ED', match: isInTransit },
    { key: 'arrived', label: 'Arrived', value: summary.arrived, color: '#9370DB', match: isArrived },
    { key: 'awaiting', label: 'Awaiting', value: summary.awaiting, color: '#FF8C00', match: (i) => isAwaitingDelivery(i) },
  ];

  const activeMatch = (FILTERS.find(f => f.key === filter) || FILTERS[0]).match;
  const filteredItems = items.filter(activeMatch);

  return (
    <div className="rounded-xl border border-[#D4A574]/30 overflow-hidden"
         style={{ background: 'linear-gradient(135deg, rgba(20,20,30,0.95) 0%, rgba(30,30,40,0.9) 100%)' }}>

      {/* Header */}
      <div className="px-6 py-4 border-b border-[#B49B7E]/20"
           style={{ background: 'linear-gradient(135deg, rgba(212, 165, 116, 0.15) 0%, rgba(180, 155, 126, 0.1) 100%)' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">📦</span>
            <div>
              <h3 className="text-[#D4A574] font-bold text-lg">Shipping &amp; Tracking</h3>
              <p className="text-gray-500 text-sm">{summary.total} ordered items</p>
            </div>
          </div>
          <button onClick={loadItems} data-testid="shipping-refresh-btn" className="text-[#D4A574] hover:text-[#B49B7E] text-sm flex items-center gap-1">
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Summary / filter tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 border-b border-[#B49B7E]/20">
        {FILTERS.map(f => (
          <div key={f.key}
               data-testid={`shipping-filter-${f.key}`}
               className={`p-3 rounded-lg cursor-pointer transition-all ${filter === f.key ? 'ring-2 ring-[#D4A574]' : ''}`}
               style={{ background: `${f.color}22` }}
               onClick={() => setFilter(filter === f.key ? 'all' : f.key)}>
            <span className="font-bold text-xl" style={{ color: f.color }}>{f.value}</span>
            <p className="text-gray-400 text-xs mt-1">{f.label}</p>
          </div>
        ))}
      </div>

      {/* Items list */}
      <div className="max-h-96 overflow-y-auto divide-y divide-[#B49B7E]/10">
        {loading ? (
          <p className="p-8 text-center text-gray-500">Loading…</p>
        ) : filteredItems.length === 0 ? (
          <p className="p-8 text-center text-gray-500">No items in this view.</p>
        ) : (
          (filteredItems || []).map(item => {
            const status = normalizeStatus(item.status);
            const color = getStatusColor(item.status);
            const trackingUrl = item.tracking_number ? getTrackingUrl(item.carrier, item.tracking_number) : null;
            return (
              <div key={item.id} className="p-4 hover:bg-black/20 transition-colors">
                <div className="flex items-center gap-4">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: color }} />
                  <div className="flex-1 min-w-0">
                    <h4 className="text-white font-medium truncate">{item.name}</h4>
                    <div className="flex items-center gap-3 text-sm text-gray-400 mt-1 flex-wrap">
                      <span className="text-xs px-2 py-0.5 rounded" style={{ background: `${color}22`, color }}>{status || '—'}</span>
                      {item.room_name && <span className="text-xs">{item.room_name}</span>}
                      {item.vendor && <span className="text-xs">{item.vendor}</span>}
                      {item.carrier && (
                        <span className="flex items-center gap-1 text-xs">{getCarrierLogo(item.carrier)} {item.carrier}</span>
                      )}
                      {item.tracking_number && (
                        trackingUrl ? (
                          <a href={trackingUrl} target="_blank" rel="noopener noreferrer"
                             data-testid={`shipping-track-link-${item.id}`}
                             className="font-mono text-xs text-[#D4A574] hover:text-[#E5B585] underline">
                            {item.tracking_number}
                          </a>
                        ) : (
                          <span className="font-mono text-xs">{item.tracking_number}</span>
                        )
                      )}
                    </div>
                  </div>
                  {(item.actual_delivery || item.expected_delivery) && (
                    <div className="text-right">
                      <p className="text-xs text-gray-500">{item.actual_delivery ? 'Delivered' : 'Est. Delivery'}</p>
                      <p className="text-[#D4A574] font-medium">
                        {new Date(item.actual_delivery || item.expected_delivery).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="px-4 py-3 border-t border-[#B49B7E]/20 bg-black/30">
        <p className="text-gray-500 text-xs text-center">
          Status is driven by the FF&amp;E sheet • Auto-refreshes every 30 seconds
        </p>
      </div>
    </div>
  );
}
